import { useCallback, useEffect, useRef, useState } from 'react';

import { LogLevel, Logger } from '@suntex.core/logging';
import type { Client, PlayerId } from '@suntex.fim/api-client';
import { type UpdateRequest, hashUpdateRequest } from '@suntex.fim/utils';

import {
  type ModulePosition,
  buildUpdateProgressRequest,
  isPracticeOnly,
  nextPracticePosition,
} from '../core/progress';

import type { InteractionSeconds } from './useInteractionSeconds';

const logger = Logger.for('useVifProgress', { level: LogLevel.Debug });

export interface UseVifProgressOptions {
  api: Client;
  playerId: PlayerId;
  moduleId: number;
  moduleVariant: number;
  /** Route-level preview: never reads or writes progress. */
  preview: boolean;
  roundsInModule: number;
  gamesPerRound: number;
  /**
   * Measured activity for the `timeOnTask` field.
   *
   * Injected rather than owned because what it may count is a property of the
   * game, not of this hook: it has to be scoped to the game surface and
   * started and stopped by the game's own lifecycle, both of which live above
   * here (see `useInteractionSeconds`, `useVifRulesModule`).
   */
  timeOnTask: InteractionSeconds;
}

export interface VifProgress {
  /** The player's position is still being read. */
  loading: boolean;
  /**
   * Nothing will be posted: a preview route, a player who has finished
   * earning, a position outside this variant's bounds, or a failed read.
   */
  practice: boolean;
  /** Legacy `gameFrame.round`. 0 means "past the last round" in practice. */
  round: number;
  /** Legacy `gameFrame.game`. */
  game: number;
  /** Stickers awarded by the last save, or `null` if none/not saved. */
  stickers: number | null;
  /**
   * A `saveGame()` write is still in flight.
   *
   * The end screen holds Next and Continue closed while it is true. The server
   * derives the next position from the `GameRecord` the save writes, so a
   * player who advances before it lands is re-read onto the game they just
   * finished (legacy blocks the same way, by only enabling Next from the save
   * response - `GameFrame.onDatabasePost`).
   */
  saving: boolean;
  /** Record the game just won. No-op in practice mode. */
  saveGame: () => void;
  /** Move to the next game: re-read the server, or count locally in practice. */
  advance: () => void;
  /** Practice-menu round pick. */
  selectRound: (round: number) => void;
}

/**
 * Reads and writes the player's position within a VIF module.
 *
 * This is the half of `GameFrame` a DOM board still needs. It is not optional
 * bookkeeping: `UpdateVIFStage` decides whether to let a player move on by
 * looking for a completed `GameRecord` for the previous game, and rolls the
 * stage *backwards* when it cannot find one. A board that plays perfectly but
 * never posts therefore loses the player's place as soon as they finish the
 * following stage.
 *
 * The server owns the counters. `POST /Player/UpdateProgress` rejects any
 * round or game that is not the `NextRound`/`NextGame` it last reported, so
 * these values are read, played, posted back, and re-read - never incremented
 * locally except in practice mode, which posts nothing.
 */
export function useVifProgress({
  api,
  playerId,
  moduleId,
  moduleVariant,
  preview,
  roundsInModule,
  gamesPerRound,
  timeOnTask,
}: UseVifProgressOptions): VifProgress {
  const [loading, setLoading] = useState(!preview);
  const [practice, setPractice] = useState(preview);
  const [position, setPosition] = useState<ModulePosition>({
    round: 1,
    game: 1,
  });
  const [stickers, setStickers] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Round-tripped back to the server untouched, as `GameFrame` does.
  const extendedData = useRef('');

  // Only the newest read may land: `advance()` can be pressed again while an
  // earlier read is still in flight.
  const readId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // The save a pending `advance()` has to wait behind. Held as a ref rather
  // than state because the wait is control flow, not something rendered.
  const pendingSave = useRef<Promise<void> | null>(null);

  const read = useCallback(async () => {
    const id = ++readId.current;
    setLoading(true);

    try {
      const response = await api.get_Player_Progress({
        playerId,
        moduleId,
        moduleVariant,
      });

      if (!mounted.current || id !== readId.current) {
        return;
      }

      extendedData.current = response.extendedData ?? '';
      setPosition({ round: response.round, game: response.game });
      setPractice(
        isPracticeOnly({
          round: response.round,
          game: response.game,
          finishedEarning: response.finishedEarning,
          playerId,
          roundsInModule,
          gamesPerRound,
        }),
      );
    } catch (error: unknown) {
      // Legacy drops into practice mode rather than blocking play
      // (`GameFrame.ts:285-293`).
      logger.warn('Failed to read module progress; entering practice mode', {
        error: String(error),
      });
      if (mounted.current && id === readId.current) {
        setPractice(true);
      }
    } finally {
      if (mounted.current && id === readId.current) {
        setLoading(false);
      }
    }
  }, [api, playerId, moduleId, moduleVariant, roundsInModule, gamesPerRound]);

  // Initial read. Preview never touches the endpoint at all.
  const started = useRef(false);
  useEffect(() => {
    if (preview || started.current) {
      return;
    }
    started.current = true;
    void read();
  }, [preview, read]);

  const saveGame = useCallback(() => {
    if (practice) {
      return;
    }

    const request = buildUpdateProgressRequest({
      playerId,
      moduleId,
      moduleVariant,
      round: position.round,
      game: position.game,
      extendedData: extendedData.current,
      timeOnTask: timeOnTask.take(),
    });

    // Signed over every field, so this must happen after the body is final.
    // `hashUpdateRequest` mutates in place and is typed against an open record;
    // the generated request has no index signature, hence the cast on the same
    // object reference. Legacy sidesteps the same mismatch with an `any` cast
    // in `GameFrame.applySaltedHash`.
    hashUpdateRequest(request as unknown as UpdateRequest, window.salter());

    setSaving(true);

    const pending = api
      .post_Player_UpdateProgress(request)
      .then((response) => {
        if (mounted.current) {
          setStickers(response.stickers);
        }
      })
      .catch((error: unknown) => {
        // A failed save leaves the server's position where it was, so the
        // re-read on `advance()` hands this same game back and the player
        // replays it. That is the legacy outcome too, and it is better than
        // stranding them on an end screen they cannot leave.
        logger.warn('Failed to save module progress', { error: String(error) });
      })
      .finally(() => {
        if (pendingSave.current === pending) {
          pendingSave.current = null;
        }
        if (mounted.current) {
          setSaving(false);
        }
      });

    pendingSave.current = pending;
  }, [api, practice, playerId, moduleId, moduleVariant, position, timeOnTask]);

  const advance = useCallback(() => {
    setStickers(null);

    if (practice) {
      setPosition((current) =>
        nextPracticePosition(current, roundsInModule, gamesPerRound),
      );
      return;
    }

    // Never overtake the save. `UpdateProgress` is what moves the server on,
    // so a read that lands first reports the position as it was before this
    // game - and the board would then deal the game that was just won.
    void (async () => {
      await pendingSave.current;

      if (mounted.current) {
        await read();
      }
    })();
  }, [practice, read, roundsInModule, gamesPerRound]);

  const selectRound = useCallback((round: number) => {
    setStickers(null);
    setPosition({ round, game: 1 });
  }, []);

  return {
    loading,
    practice,
    round: position.round,
    game: position.game,
    stickers,
    saving,
    saveGame,
    advance,
    selectRound,
  };
}
